import React, {
  useEffect,
  useState,
  useContext,
  forwardRef,
  useImperativeHandle,
} from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import FaAdditionsSchedule from "../../../components/Schedules/CA/FaAddition/FaAdditionsSchedule";
import { getCaSettings } from "../../../builders/settings/CaSetting";
import ComponentLoading from "../../../components/App/ComponentLoading";
import { useErrorDialog } from "../../../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../../../contexts/SuccessDialogContext";
import { useAccessControl } from "../../../utils/hooks/useAccessControl";
import { CA_SCHEDULES } from "../../../utils/data/controlData";

const getRegisteredAssetSourceId = (row, index) =>
  `fa-register-addition-${row.assetId || row.rowKey || index}`;

const getRegisteredAssetCategoryId = (row, index) =>
  row.categoryId ||
  row.assetCategoryId ||
  `fa-register-category-${String(row.assetCategory || row.category || index)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")}`;

const buildRegisteredAssetSources = (faRegisterImport) => {
  const registeredRows =
    faRegisterImport?.faAdditions ||
    faRegisterImport?.summaries?.faAdditions ||
    [];

  const categoryMap = new Map();
  const additionsDetails = registeredRows.map((row, index) => {
    const registeredAssetSourceId = getRegisteredAssetSourceId(row, index);
    const categoryId = getRegisteredAssetCategoryId(row, index);
    const assetCategory =
      row.assetCategory || row.category || "Registered Assets";
    const detail = {
      assetId: registeredAssetSourceId,
      categoryId,
      assetCategory,
      registeredAssetSourceId,
      isImportedFromFARegister: true,
      faRegisterSource: true,
      faRegisterSourceDetail: true,
      locked: true,
      description: row.description || "",
      cost: Number(row.cost || 0),
      isDeferred: row.deferred ?? false,
      CaName: row.caName || "",
      numberOfYears: row.numberOfYears ?? row.noOfYearsClaim ?? 0,
    };

    if (!categoryMap.has(categoryId)) {
      categoryMap.set(categoryId, {
        assetId: categoryId,
        categoryId,
        assetCategory,
        isImportedFromFARegister: true,
        faRegisterSource: true,
        locked: true,
        additionsDetails: [],
      });
    }

    categoryMap.get(categoryId).additionsDetails.push(detail);
    return detail;
  });

  const categories = Array.from(categoryMap.values()).map((category) => {
    const cost = category.additionsDetails.reduce(
      (total, detail) => total + (Number(detail.cost) || 0),
      0,
    );
    return {
      ...category,
      cost: { additions: cost },
      totalFloating: cost,
      isCostAligned: true,
      isAllDeferred:
        category.additionsDetails.length > 0 &&
        category.additionsDetails.every((detail) => detail.isDeferred === true),
    };
  });

  return {
    source: "FA Register",
    additionsDetails,
    categories,
    assetCategories: faRegisterImport?.assetCategories || [],
  };
};

const FaAdditionsSchedulePage = forwardRef(
  ({ company, scheduleId, openScheduleWithDelay }, ref) => {
    const {
      getCAScheduleDataByType,
      updateCASchedule,
      deleteCAScheduleByType,
    } = useContext(AuthContext);
    const [schedule, setSchedule] = useState({});
    const [scheduleDetails, setScheduleDetails] = useState({});
    const [reloadKey, setReloadKey] = useState(0);

    const { canEdit: canEditCASchedule } = useAccessControl(CA_SCHEDULES);

    const [loading, setLoading] = useState(true);

    const handleError = useErrorDialog();
    const handleSuccess = useSuccessDialog();

    const [isDirty, setIsDirty] = useState(false);
    const [unSavedData, setUnSavedData] = useState(null);
    useImperativeHandle(ref, () => ({
      isDirty: () => isDirty,
      getUnsavedData: () => {
        return unSavedData;
      },
      clearDirtyFlag: () => {
        setIsDirty(false);
      },
    }));
    const recordChangeHappened = () => setIsDirty(true);
    const recordSaved = () => setIsDirty(false);
    const recordUnsavedData = (data) =>
      setUnSavedData({
        ...data,
        sch_id: scheduleId,
        scheduleType: "FAAdditions",
      });

    const saveSchedule = async (scheduleDetails) => {
      if (!company || !scheduleId) {
        handleError("Missing UEN or Schedule ID. Please try again.");
        return;
      }

      const updatedSchedule = {
        sch_id: scheduleId,
        scheduleType: "FAAdditions",
        dataRows: scheduleDetails.dataRows,
        otherSourceRows: scheduleDetails.otherSourceRows,
        registeredAssetSourceRows: scheduleDetails.registeredAssetSourceRows,
        faAdditionsSummary: scheduleDetails.faAdditionsSummary,
        hirePurchaseSummary: scheduleDetails.hirePurchaseSummary,
        totalRenovation: scheduleDetails.totalRenovation,
        totalRevenue: scheduleDetails.totalRevenue,
        doc: scheduleDetails.doc,
        notes: scheduleDetails.notes,
        reviewNotes: scheduleDetails.reviewNotes,
      };

      setLoading(true);
      const response = await updateCASchedule(updatedSchedule);
      setLoading(false);

      if (response) {
        handleSuccess("The schedule has been saved successfully.");
      } else {
        handleError(
          "There was a problem saving the schedule. Please contact support if the issue persists.",
        );
      }

      setReloadKey((k) => k + 1);
    };

    const deleteSchedule = async () => {
      if (!scheduleId) {
        handleError("Missing Schedule ID.");
        return;
      }

      const updatedSchedule = {
        sch_id: scheduleId,
      };

      setLoading(true);
      const response = await deleteCAScheduleByType(updatedSchedule);
      setLoading(false);

      if (response) {
        handleSuccess("Schedule deleted successfully.");
        localStorage.removeItem("selectedSection");
        localStorage.setItem("selectedComponent", "Detailed P&L");
        window.location.reload();
      } else {
        handleError(
          "There was a problem saving the schedule. Please contact support if the issue persists.",
        );
      }
    };

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);

        try {
          // Fetch schedule data from API
          const response = await getCAScheduleDataByType(
            scheduleId,
            "FAAdditions",
          );

          const scheduleData = response?.schedule;

          let caSettings = getCaSettings();

          if (scheduleData) {
            setSchedule({
              id: scheduleId,
              type: "Fixed Asset Additions",
              scheduleNo: scheduleData.scheduleNo,
              name: "Fixed Asset Additions",
              caSettings: scheduleData.caSetting || caSettings,
              refSources: scheduleData.scheduleMapping || {},
              notes: scheduleData.notes || "",
              reviewNotes: scheduleData.reviewNotes || "",
            });

            const registeredAssetSources = buildRegisteredAssetSources(
              scheduleData.faRegisterImport,
            );

            setScheduleDetails({
              dataRows: scheduleData.dataRows || [],
              otherSources: scheduleData.otherSourceRows || {
                source: "P&L",
                additionsDetails: [],
              },
              registeredAssetSources,
            });
          }
        } catch (error) {
          handleError(error);
        } finally {
          setLoading(false);
        }
      };

      if (company && scheduleId) {
        fetchData();
      }
    }, [scheduleId, company.uen, company.id, reloadKey]);

    return (
      <>
        {loading ? (
          <ComponentLoading />
        ) : (
          <FaAdditionsSchedule
            company={company}
            schedule={schedule}
            scheduleDetails={scheduleDetails}
            saveSchedule={saveSchedule}
            deleteSchedule={deleteSchedule}
            openScheduleWithDelay={openScheduleWithDelay}
            recordSaved={recordSaved}
            recordUnsavedData={recordUnsavedData}
            recordChangeHappened={recordChangeHappened}
            canEditSchedule={canEditCASchedule}
          />
        )}
      </>
    );
  },
);

export default FaAdditionsSchedulePage;
